import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { QueueView } from '@hone-automation/common';

/** Operation types that can be performed on requests */
export type BatchOperationType = 'setStatus' | 'setPriority' | 'setAssignee' | 'setParentId' | 'resolve';

/** Status values for setStatus operation */
export type StatusValue = 'Open' | 'In Progress' | 'Closed';

/** Priority values for setPriority operation */
export type PriorityValue = 'Blocker' | 'Normal';

/** Assignee values for setAssignee operation - 'me' is resolved to current user ID */
export type AssigneeValue = string | null;

/** ParentId values for setParentId operation - group ID or null to ungroup */
export type ParentIdValue = string | null;

/** All possible operation values */
export type BatchOperationValue = StatusValue | PriorityValue | AssigneeValue | ParentIdValue;

/** A single operation in the batch queue */
export interface BatchOperation {
  id: string;
  requestId: string;
  type: BatchOperationType;
  value: BatchOperationValue;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string;
}

/** Progress information for the current batch */
export interface BatchProgress {
  total: number;
  completed: number;
  errors: number;
  isProcessing: boolean;
  /** Target filter where items are being moved */
  targetFilter: QueueView | null;
}

/** Error summary after batch completion */
export interface BatchError {
  requestId: string;
  error: string;
}

interface BatchQueueContextValue {
  /** Current batch progress */
  progress: BatchProgress;
  /** Errors from the last batch */
  errors: BatchError[];
  /** Add operations to the queue and start processing */
  addBatch: (
    requestIds: string[],
    operation: BatchOperationType,
    value: BatchOperationValue,
    targetFilter: QueueView,
    onProcess: (requestId: string, operation: BatchOperationType, value: BatchOperationValue) => Promise<void>
  ) => void;
  /** Cancel the current batch (stops processing new items) */
  cancelBatch: () => void;
  /** Clear errors after displaying them */
  clearErrors: () => void;
  /** Check if a specific filter is currently receiving items */
  isFilterProcessing: (filter: QueueView) => boolean;
}

const BatchQueueContext = createContext<BatchQueueContextValue | null>(null);

/**
 * Hook to access batch queue context
 */
export const useBatchQueueContext = () => {
  const context = useContext(BatchQueueContext);
  if (!context) {
    throw new Error('useBatchQueueContext must be used within a BatchQueueProvider');
  }
  return context;
};

/**
 * Optional hook that returns null if outside provider
 */
export const useOptionalBatchQueueContext = () => {
  return useContext(BatchQueueContext);
};

interface BatchQueueProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for batch queue operations
 * Processes operations sequentially to avoid overwhelming the API
 */
export const BatchQueueProvider: React.FC<BatchQueueProviderProps> = ({ children }) => {
  const [progress, setProgress] = useState<BatchProgress>({
    total: 0,
    completed: 0,
    errors: 0,
    isProcessing: false,
    targetFilter: null,
  });
  const [errors, setErrors] = useState<BatchError[]>([]);

  // Use refs to track cancellation and processing state
  const cancelledRef = useRef(false);
  const isProcessingRef = useRef(false);

  const addBatch = useCallback(
    async (
      requestIds: string[],
      operation: BatchOperationType,
      value: BatchOperationValue,
      targetFilter: QueueView,
      onProcess: (requestId: string, operation: BatchOperationType, value: BatchOperationValue) => Promise<void>
    ) => {
      if (requestIds.length === 0) return;

      // Guard against concurrent batches - reject if already processing
      if (isProcessingRef.current) {
        console.warn('BatchQueueContext: Cannot start new batch while another is processing');
        return;
      }

      // Reset state
      isProcessingRef.current = true;
      cancelledRef.current = false;
      setErrors([]);
      setProgress({
        total: requestIds.length,
        completed: 0,
        errors: 0,
        isProcessing: true,
        targetFilter,
      });

      const batchErrors: BatchError[] = [];

      // Process sequentially
      for (let i = 0; i < requestIds.length; i++) {
        // Check for cancellation
        if (cancelledRef.current) {
          break;
        }

        const requestId = requestIds[i];

        try {
          await onProcess(requestId, operation, value);

          setProgress(prev => ({
            ...prev,
            completed: prev.completed + 1,
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          batchErrors.push({ requestId, error: errorMessage });

          setProgress(prev => ({
            ...prev,
            completed: prev.completed + 1,
            errors: prev.errors + 1,
          }));
        }
      }

      // Batch complete
      isProcessingRef.current = false;
      setProgress(prev => ({
        ...prev,
        isProcessing: false,
      }));

      if (batchErrors.length > 0) {
        setErrors(batchErrors);
      }
    },
    []
  );

  const cancelBatch = useCallback(() => {
    cancelledRef.current = true;
    isProcessingRef.current = false;
    setProgress(prev => ({
      ...prev,
      isProcessing: false,
    }));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const isFilterProcessing = useCallback(
    (filter: QueueView) => {
      return progress.isProcessing && progress.targetFilter === filter;
    },
    [progress.isProcessing, progress.targetFilter]
  );

  const value: BatchQueueContextValue = useMemo(
    () => ({
      progress,
      errors,
      addBatch,
      cancelBatch,
      clearErrors,
      isFilterProcessing,
    }),
    [progress, errors, addBatch, cancelBatch, clearErrors, isFilterProcessing]
  );

  return <BatchQueueContext.Provider value={value}>{children}</BatchQueueContext.Provider>;
};
